import { Api } from '../api';
import { mapDataToInputItems } from '../filter/helpers';
import { ListInputItemOptions } from '../filter/model';
import { addListInputItems } from '../filter/slice';
import {
  BulkImportDataRequest,
  BulkImportDataResponse,
  BulkImportListRequest,
  BulkImportListResponse,
  BulkRecordMappingStatusTextEnum,
  BulkUploadRequest,
  DownloadErpFileRequest,
  ERPUploadBatchUpdateRequest,
  ERPUploadBatchUpdateResponse,
  ErpFileUploadRequest,
  ErpFileUploadResponse,
  ErpUploadBatchDetailsRequest,
  ErpUploadBatchDetailsResponse,
  GetErpColumnMappingRequest,
  GetErpColumnMappingResponse,
  ListErpUploadsRequest,
  ListErpUploadsResponse,
  UpdateColumnMappingRequest,
  UpdateErpSyncSettingsResponse,
  UpdateErpSyncSettingsRequest,
  GetErpSyncSettingResponse,
  GetAppsListResponse,
  UninstallAppResponse,
  InstallAppResponse,
  GetCredentialsResponse,
  GenerateCredentialResponse,
  ChangeApiKeyStatusResponse,
  DeleteApiKeyResponse,
  GetPdfCoordinatesRequest,
  GetPdfCoordinatesResponse,
  GetSFTPCredentialsResponse,
  GetColumnMappingStatusResponse,
  UpdateRazorPayCredentialsRequest,
  GetRazorPayCredentialsResponse,
  GetRazorPayCredentialsRequest,
  ErpSystemsAppEnum,
  UploadPdfTemplateRequest,
  GetPdfTemplateResponse,
  GetPdfTemplateRequest,
  UpdatePdfTemplateMappingRequest,
  GetZosyncSettingsResponse,
  UpdateZosyncSettingsRequest,
  GetZoApiWebhookConfigRequest,
  GetZoApiWebhookConfigResponse,
  UpdateZoApiWebhookConfigResponse,
  UpdateZoApiWebhookConfigRequest,
  SaveErpNextCredentialsResponse,
  SaveErpNextCredentialsRequest,
  GetErpNextCredentialsResponse,
  GetErpNextCredentialsRequest,
  SaveFetchDateRequest,
  GetErpCompaniesResponse,
  GetErpExportMappingsRequest,
  ErpExportMappingResponse,
  UpdateErpExportMappingsRequest,
  UpdateERPAuthenticationRequest,
  GetERPAuthenticationResponse,
  ConnectGoogleRequest,
  ConnectGoogleResponse,
  GetConnectorsRequest,
  GetConnectorsResponse,
  GetGoogleSheetsPickerConfigRequest,
  GetGoogleSheetsPickerConfigResponse,
  RegisterGoogleSheetRequest,
  RegisterGoogleSheetResponse,
  DisconnectGoogleSheetsRequest,
  DisconnectGoogleSheetsResponse,
  GetGoogleSheetResponse,
  GetGoogleSheetRequest,
  DeleteGoogleRequest,
} from './model';

export const bulkImportApi = Api.injectEndpoints({
  endpoints: (builder) => ({
    bulkUpload: builder.mutation<any, BulkUploadRequest>({
      query(args) {
        return {
          url: `mdm-integration/uploadbatch`,
          method: 'POST',
          body: args,
        };
      },
    }),
    getBulkImportsList: builder.query<
      BulkImportListResponse,
      BulkImportListRequest
    >({
      query(arg) {
        const { body, ...rest } = arg;
        return {
          url: `mdm-integration/uploadbatch/history`,
          method: 'POST',
          params: rest,
          body,
        };
      },
    }),
    getBulkImportData: builder.query<
      BulkImportDataResponse,
      BulkImportDataRequest
    >({
      query(arg) {
        return {
          url: `mdm-integration/uploadbatch/details/${arg?.uploadBatchId}`,
          method: 'POST',
          params: arg?.params,
          body: arg?.body,
        };
      },
      async onQueryStarted(request, { dispatch, queryFulfilled }) {
        queryFulfilled.then((response) => {
          const customerFirmName = response?.data?.customerFirmName?.map(
            (item) => ({
              id: item,
              name: item,
            })
          );

          const erpCustomerName = mapDataToInputItems(
            customerFirmName,
            'id',
            'name'
          )?.filter((x: any) => x.value);

          dispatch(
            addListInputItems({
              inputItems: erpCustomerName,
              key: ListInputItemOptions.erpCustomerName,
            })
          );

          const mappingStatus = response?.data?.mapping_status?.map((item) => ({
            id: item,
            name: BulkRecordMappingStatusTextEnum[item],
          }));

          const erpMappingStatus = mapDataToInputItems(
            mappingStatus,
            'id',
            'name'
          )?.filter((x: any) => x.value);

          dispatch(
            addListInputItems({
              inputItems: erpMappingStatus,
              key: ListInputItemOptions.erpMappingStatus,
            })
          );

          const transactionType = response?.data?.transactionType?.map(
            (item) => ({
              id: item,
              name: item,
            })
          );

          const erpTransactionType = mapDataToInputItems(
            transactionType,
            'id',
            'name'
          )?.filter((x: any) => x.value);

          dispatch(
            addListInputItems({
              inputItems: erpTransactionType,
              key: ListInputItemOptions.erpTransactionType,
            })
          );

          const uploadStatus = response?.data?.upload_status?.map((item) => ({
            id: item,
            name: item,
          }));

          const taskStatus = mapDataToInputItems(
            uploadStatus,
            'id',
            'name'
          )?.filter((x: any) => x.value);

          dispatch(
            addListInputItems({
              inputItems: taskStatus,
              key: ListInputItemOptions.erpTaskStatus,
            })
          );
        });
      },
    }),
    erpFileUpload: builder.mutation<
      ErpFileUploadResponse,
      ErpFileUploadRequest
    >({
      query(args) {
        return {
          url: `mdm-integration/erp-sync/file-upload/${args?.workspaceId}`,
          method: 'POST',
          params: args?.params,
          body: args?.body,
        };
      },
    }),
    listErpUploads: builder.mutation<
      ListErpUploadsResponse,
      ListErpUploadsRequest
    >({
      query(args) {
        return {
          url: `mdm-integration/erp-sync/history/${args?.workspaceId}`,
          method: 'POST',
          params: args?.params,
          body: args?.body,
        };
      },
    }),
    downloadErpUploadedFile: builder.mutation<any, DownloadErpFileRequest>({
      query(arg) {
        return {
          url: `mdm-integration/erp-sync/details/download/${arg?.id}`,
          params: arg?.params,
          method: 'POST',
          // responseHandler: (response) => response.,
        };
      },
    }),
    erpUploadBatchDetails: builder.mutation<
      ErpUploadBatchDetailsResponse,
      ErpUploadBatchDetailsRequest
    >({
      query(args) {
        return {
          url: `mdm-integration/erp-sync/details/${args?.erpBatchId}`,
          method: 'POST',
          params: args?.params,
        };
      },
    }),
    erpUploadBatchUpdate: builder.mutation<
      ERPUploadBatchUpdateResponse,
      ERPUploadBatchUpdateRequest
    >({
      query(arg) {
        return {
          url: `mdm-integration/uploadbatch/update/${arg?.uploadBatchId}`,
          method: 'POST',
          params: arg?.params,
          body: arg?.body,
        };
      },
    }),
    updateColumnMapping: builder.mutation<any, UpdateColumnMappingRequest>({
      query(arg) {
        return {
          url: `mdm-integration/erp-sync/column-mapping`,
          method: 'POST',
          body: arg?.body,
          params: arg?.params,
        };
      },
    }),
    getErpColumnMapping: builder.mutation<
      GetErpColumnMappingResponse,
      GetErpColumnMappingRequest
    >({
      query(arg) {
        return {
          url: `mdm-integration/erp-sync/column-mapping`,
          method: 'GET',
          params: arg?.params,
        };
      },
    }),
    updateErpSyncSettings: builder.mutation<
      UpdateErpSyncSettingsResponse,
      UpdateErpSyncSettingsRequest
    >({
      query(arg) {
        return {
          url: `/mdm-integration/erp-sync/settings`,
          method: 'POST',
          body: arg?.body,
          params: arg?.params,
        };
      },
    }),

    getErpSyncSettings: builder.query<
      GetErpSyncSettingResponse,
      {
        params: {
          sellerWorkspaceId: string;
          erpSystemCode: string;
        };
      }
    >({
      query(args) {
        return {
          url: 'mdm-integration/erp-sync/settings',
          method: 'GET',
          params: args?.params,
        };
      },
    }),
    getAppsList: builder.query<
      GetAppsListResponse,
      {
        params: {
          sellerWorkspaceId: string;
        };
      }
    >({
      query(args) {
        return {
          url: '/mdm-integration/erp-systems',
          method: 'GET',
          params: args?.params,
        };
      },
    }),
    getSecureSmsConfig: builder.query<
      any,
      {
        params: {
          sellerWorkspaceId: string;
        };
        /**
         * SMS provider code, e.g. "securesms" or "infobip".
         * Defaults to "securesms" for backward compatibility.
         */
        provider?: string;
      }
    >({
      query(args) {
        const provider = args?.provider || 'securesms';
        return {
          url: `/mdm-integration/erp-systems/sms/${provider}`,
          method: 'GET',
          params: args?.params,
        };
      },
    }),
    updateSecureSmsConfig: builder.mutation<
      any,
      {
        body: {
          sellerWorkspaceId: string;
          config: {
            baseUrl: string;
            apiKey: string;
          };
        };
        /**
         * SMS provider code, e.g. "securesms" or "infobip".
         * Defaults to "securesms" for backward compatibility.
         */
        provider?: string;
      }
    >({
      query(arg) {
        const provider = arg?.provider || 'securesms';
        return {
          url: `/mdm-integration/erp-systems/sms/${provider}`,
          method: 'POST',
          body: arg.body,
        };
      },
    }),
    installApp: builder.mutation<
      InstallAppResponse,
      {
        body: {
          sellerWorkspaceId: string;
          appId: string;
        };
      }
    >({
      query(arg) {
        return {
          url: `/mdm-integration/erp-systems/install`,
          method: 'POST',
          body: arg?.body,
        };
      },
    }),
    uninstallApp: builder.mutation<
      UninstallAppResponse,
      {
        body: {
          sellerWorkspaceId: string;
          appId: string;
        };
      }
    >({
      query(arg) {
        return {
          url: `/mdm-integration/erp-systems/uninstall`,
          method: 'DELETE',
          body: arg?.body,
        };
      },
    }),
    getCredentials: builder.query<
      GetCredentialsResponse,
      {
        params: {
          sellerWorkspaceId: string;
          label?: ErpSystemsAppEnum;
        };
      }
    >({
      query(args) {
        return {
          url: '/mdm-integration/v1/api/auth/credentials',
          method: 'GET',
          params: args?.params,
        };
      },
    }),
    generateCredentials: builder.mutation<
      GenerateCredentialResponse,
      {
        body: {
          workspaceId: string;
          label: ErpSystemsAppEnum;
        };
      }
    >({
      query(arg) {
        return {
          url: '/mdm-integration/v1/api/auth/credentials',
          method: 'POST',
          body: arg?.body,
        };
      },
    }),
    changeApiKeyStatus: builder.mutation<
      ChangeApiKeyStatusResponse,
      {
        body: {
          sellerWorkspaceId: string;
          clientId: string;
          isActive: boolean;
        };
      }
    >({
      query(arg) {
        return {
          url: '/mdm-integration/v1/api/auth/credentials/status',
          method: 'POST',
          body: arg?.body,
        };
      },
    }),
    deleteApiKey: builder.mutation<
      DeleteApiKeyResponse,
      {
        clientId: string;
        params: {
          sellerWorkspaceId: string;
        };
      }
    >({
      query(arg) {
        return {
          url: `/mdm-integration/v1/api/auth/credentials/${arg?.clientId}`,
          method: 'DELETE',
          params: arg?.params,
        };
      },
    }),
    updateProcessingStatus: builder.mutation<
      any,
      {
        uploadBatchId: string;
        params: {
          entity: string;
          selectAll: boolean;
        };
      }
    >({
      query(arg) {
        return {
          url: `/mdm-integration/uploadbatch/processing-status/${arg?.uploadBatchId}`,
          method: 'PUT',
          params: arg?.params,
        };
      },
    }),
    uploadPdfTemplate: builder.mutation<any, UploadPdfTemplateRequest>({
      query(arg) {
        return {
          url: `/mdm-integration/api/pdf-templates/${arg.sellerWorkspaceId}`,
          method: 'POST',
          body: arg?.body,
          params: arg?.params,
        };
      },
    }),
    getPdfTemplate: builder.query<
      GetPdfTemplateResponse,
      GetPdfTemplateRequest
    >({
      query(arg) {
        return {
          url: `/mdm-integration/api/pdf-templates/${arg.sellerWorkspaceId}?type=${arg.type}`,
          method: 'GET',
        };
      },
    }),
    getPdfCoordinates: builder.mutation<
      GetPdfCoordinatesResponse,
      GetPdfCoordinatesRequest
    >({
      query(arg) {
        return {
          url: `/mdm-integration/api/pdf-templates/${arg?.sellerWorkspaceId}/regex/coordinates`,
          method: 'POST',
          body: arg?.body,
          params: arg?.params,
        };
      },
    }),
    updatePdfTemplateMapping: builder.mutation<
      any,
      UpdatePdfTemplateMappingRequest
    >({
      query(arg) {
        return {
          url: `mdm-integration/api/pdf-templates/${arg?.workspaceId}/mappings`,
          method: 'POST',
          body: arg?.body,
          params: arg?.params,
        };
      },
    }),
    generateSFTPPassword: builder.mutation<
      { password: string },
      {
        params: {
          sellerWorkspaceId: string;
        };
      }
    >({
      query(arg) {
        return {
          url: `/mdm-integration/v1/api/auth/credentials/sftp-password`,
          params: arg.params,
          method: 'POST',
        };
      },
    }),
    getSFTPCredentials: builder.query<
      GetSFTPCredentialsResponse,
      {
        params: {
          sellerWorkspaceId: string;
        };
      }
    >({
      query(arg) {
        return {
          url: `/mdm-integration/v1/api/auth/sftp-credentials`,
          params: arg?.params,
          method: 'GET',
        };
      },
    }),
    getColumnMappingStatus: builder.query<
      GetColumnMappingStatusResponse,
      {
        params: {
          sellerWorkspaceId: string;
        };
      }
    >({
      query(arg) {
        return {
          url: `mdm-integration/erp-sync/column-mapping/status`,
          params: arg?.params,
          method: 'GET',
        };
      },
    }),
    getErpCompanies: builder.query<
      GetErpCompaniesResponse,
      {
        params: {
          sellerWorkspaceId: string;
        };
      }
    >({
      query(arg) {
        return {
          url: `/hub/erp-integration/api/erpnext/companies`,
          params: arg?.params,
          method: 'GET',
        };
      },
    }),
    updateRazorPayCredentials: builder.mutation<
      any,
      UpdateRazorPayCredentialsRequest
    >({
      query(arg) {
        return {
          url: `/mdm-integration/erp-systems/activateRazorPay`,
          method: 'POST',
          params: { sellerWorkspaceId: arg.sellerWorkspaceId },
          body: arg,
        };
      },
    }),
    getRazorPayCredentials: builder.query<
      GetRazorPayCredentialsResponse,
      GetRazorPayCredentialsRequest
    >({
      query(arg) {
        return {
          url: `/mdm-integration/erp-systems/razorPayDetails`,
          method: 'GET',
          params: arg.params,
        };
      },
    }),
    addOrgId: builder.mutation<
      any,
      {
        params: {
          sellerWorkspaceId: string;
        };
        body: {
          organizationId: string;
        };
      }
    >({
      query(arg) {
        return {
          url: `/hub/erp-integration/api/zoho/zoho-books/organization/${arg.params.sellerWorkspaceId}`,
          method: 'PATCH',
          body: arg.body,
          params: arg.params,
        };
      },
    }),
    getZohoAuthState: builder.mutation<
      {
        isAuthorized: boolean;
        id: string;
        workspaceId: string;
        organizationId: string;
        orgName: string;
        masters: Record<string, string>;
      },
      {
        params: {
          sellerWorkspaceId: string;
        };
      }
    >({
      query(arg) {
        return {
          url: `/hub/erp-integration/api/zoho/zoho-books/account/${arg.params.sellerWorkspaceId}`,
          method: 'GET',
          params: arg.params,
        };
      },
    }),
    getZohoAuthUrl: builder.query<
      {
        url: string;
      },
      {
        params: {
          sellerWorkspaceId: string;
        };
      }
    >({
      query(arg) {
        return {
          url: `/hub/erp-integration/api/zoho/auth/url/${arg.params.sellerWorkspaceId}`,
          method: 'GET',
          params: arg.params,
        };
      },
    }),
    getZosyncSettings: builder.query<
      GetZosyncSettingsResponse,
      {
        sellerWorkspaceId: string;
      }
    >({
      query(arg) {
        return {
          url: `/mdm-integration/zosync/${arg.sellerWorkspaceId}/settings`,
          method: 'GET',
        };
      },
    }),
    updateZosyncSettings: builder.mutation<any, UpdateZosyncSettingsRequest>({
      query(arg) {
        return {
          url: `/mdm-integration/zosync/${arg.params.sellerWorkspaceId}/settings`,
          method: 'POST',
          params: arg.params,
          body: arg.body,
        };
      },
    }),
    getZoApiWebhookConfig: builder.query<
      GetZoApiWebhookConfigResponse,
      GetZoApiWebhookConfigRequest
    >({
      query(arg) {
        return {
          url: `/mdm-integration/webhooks/${arg.sellerWorkspaceId}`,
          method: 'GET',
          params: arg.params,
        };
      },
    }),
    updateZoApiWebhookConfig: builder.mutation<
      UpdateZoApiWebhookConfigResponse,
      UpdateZoApiWebhookConfigRequest
    >({
      query(body) {
        return {
          url: `/mdm-integration/webhooks`,
          method: 'POST',
          body,
        };
      },
    }),
    getErpNextCredentials: builder.query<
      GetErpNextCredentialsResponse,
      GetErpNextCredentialsRequest
    >({
      query(arg) {
        return {
          url: `/hub/erp-integration/api/credentials`,
          method: 'GET',
          params: arg.params,
        };
      },
    }),

    saveErpNextCredentials: builder.mutation<
      SaveErpNextCredentialsResponse,
      SaveErpNextCredentialsRequest
    >({
      query(arg) {
        return {
          url: `/hub/erp-integration/api/credentials`,
          method: 'POST',
          params: arg.params,
          body: arg.body,
        };
      },
    }),

    saveFetchDate: builder.mutation<any, SaveFetchDateRequest>({
      query(arg) {
        const params = new URLSearchParams();

        params.append('provider', arg.params.provider);
        params.append('sellerWorkspaceId', arg.params.sellerWorkspaceId);

        if (arg.params.startDate) {
          params.append('startDate', arg.params.startDate);
        }

        if (arg.params.masters?.length) {
          const mastersString = `[${arg.params.masters.join(',')}]`;
          params.append('masters', mastersString);
        }

        return {
          url: `/hub/erp-integration/api/historical-import`,
          method: 'POST',
          params,
        };
      },
    }),
    configureErpNextWebhooks: builder.mutation<
      any,
      {
        params: {
          sellerWorkspaceId: string;
        };
      }
    >({
      query(arg) {
        return {
          url: `/hub/erp-integration/api/erpnext/configure/webhooks`,
          method: 'POST',
          params: arg.params,
        };
      },
    }),
    getErpExportMappings: builder.query<
      ErpExportMappingResponse,
      GetErpExportMappingsRequest
    >({
      query(arg) {
        return {
          url: `/mdm-integration/erp-export-mappings`,
          method: 'GET',
          params: arg,
        };
      },
    }),
    updateErpExportMappings: builder.mutation<
      ErpExportMappingResponse,
      UpdateErpExportMappingsRequest
    >({
      query(arg) {
        return {
          url: `/mdm-integration/erp-export-mappings`,
          method: 'POST',
          params: {
            sellerWorkspaceId: arg.sellerWorkspaceId,
            entity: arg.entity,
            voucherType: arg.voucherType,
          },
          body: arg.body,
        };
      },
    }),

    updateERPAuthentication: builder.mutation<
      GetZoApiWebhookConfigResponse,
      UpdateERPAuthenticationRequest
    >({
      query(arg) {
        return {
          url: '/hub/erp-integration/api/erp-authentications',
          method: 'POST',
          params: arg?.params,
          body: arg?.body,
        };
      },
    }),

    getERPAuthentication: builder.query<
      GetERPAuthenticationResponse,
      {
        params: {
          sellerWorkspaceId: string;
          companyCode: string;
          erpId: string;
        };
      }
    >({
      query(arg) {
        return {
          url: '/hub/erp-integration/api/erp-authentications',
          method: 'GET',
          params: arg?.params,
        };
      },
    }),

    updateSAPHistoricalImport: builder.mutation<
      any,
      {
        params: {
          provider: string;
          sellerWorkspaceId: string;
          startDate: string;
          masters?: string;
        };
      }
    >({
      query(arg) {
        return {
          url: '/hub/erp-integration/api/historical-import',
          method: 'POST',
          params: arg?.params,
        };
      },
    }),

    startSAPSync: builder.mutation<
      any,
      {
        body: {
          workspaceId: string;
          companyCode: string;
          erpId: string;
          entities: string[];
          endpointId: any;
          pollIntervalSec: number;
          cursor: any;
          metadata: any;
          isActive: boolean;
        };
        params: {
          sellerWorkspaceId: string;
        };
      }
    >({
      query(arg) {
        return {
          url: 'hub/erp-integration/api/poll/start',
          method: 'POST',
          body: arg?.body,
          params: arg?.params,
        };
      },
    }),

    stopSAPSync: builder.mutation<
      any,
      {
        body: {
          workspaceId: string;
          companyCode: string;
          erpId: string;
          entities?: string[];
          endpointId: any;
          pollIntervalSec: number;
          cursor: any;
          metadata: any;
          isActive: boolean;
        };
        params: {
          sellerWorkspaceId: string;
        };
      }
    >({
      query(arg) {
        return {
          url: 'hub/erp-integration/api/poll/stop',
          method: 'POST',
          body: arg?.body,
          params: arg?.params,
        };
      },
    }),
    connectGoogle: builder.mutation<
      ConnectGoogleResponse,
      ConnectGoogleRequest
    >({
      query(arg) {
        return {
          url: 'hub/copilot/connectors/connect',
          method: 'POST',
          params: { sellerWorkspaceId: arg.workspace_id },
          body: arg,
        };
      },
      invalidatesTags: ['Connectors'],
    }),

    disconnectGoogle: builder.mutation<
      DisconnectGoogleSheetsResponse,
      DisconnectGoogleSheetsRequest
    >({
      query(arg) {
        return {
          url: 'hub/copilot/connectors/disconnect',
          method: 'POST',
          body: arg,
          params: { sellerWorkspaceId: arg.workspace_id },
        };
      },
      invalidatesTags: ['Connectors'],
    }),

    getConnectors: builder.query<GetConnectorsResponse[], GetConnectorsRequest>(
      {
        query(arg) {
          return {
            url: 'hub/copilot/connectors',
            method: 'GET',
            params: {
              workspace_id: arg.workspace_id,
              sellerWorkspaceId: arg.workspace_id,
            },
          };
        },
        providesTags: ['Connectors'],
      }
    ),

    getGoogleSheetsPickerConfig: builder.query<
      GetGoogleSheetsPickerConfigResponse,
      GetGoogleSheetsPickerConfigRequest
    >({
      query(arg) {
        return {
          url: 'hub/copilot/connectors/config',
          method: 'GET',
          params: {
            workspace_id: arg.workspace_id,
            connector_type: arg.connector_type,
            sellerWorkspaceId: arg.workspace_id,
          },
        };
      },
    }),

    registerGoogleSheet: builder.query<
      RegisterGoogleSheetResponse,
      RegisterGoogleSheetRequest
    >({
      query(arg) {
        return {
          url: `hub/copilot/connectors/google/sheets/${arg.spreadsheet_id}`,
          method: 'GET',
          params: {
            workspace_id: arg.workspace_id,
            sellerWorkspaceId: arg.workspace_id,
          },
        };
      },
    }),

    getGoogleSheet: builder.query<
      GetGoogleSheetResponse[],
      GetGoogleSheetRequest
    >({
      query(arg) {
        return {
          url: `hub/copilot/connectors/google/sheets`,
          method: 'GET',
          params: {
            workspace_id: arg.workspace_id,
            sellerWorkspaceId: arg.workspace_id,
          },
        };
      },
    }),
    deleteGoogleSheet: builder.mutation<any, DeleteGoogleRequest>({
      query(arg) {
        return {
          url: `hub/copilot/connectors/google/sheets/${arg.spreadsheet_id}`,
          method: 'DELETE',
          params: {
            workspace_id: arg.workspace_id,
            sellerWorkspaceId: arg.workspace_id,
          },
        };
      },
    }),
  }),
});

export const {
  useBulkUploadMutation,
  useLazyGetBulkImportsListQuery,
  useLazyGetBulkImportDataQuery,
  useErpFileUploadMutation,
  useListErpUploadsMutation,
  useErpUploadBatchDetailsMutation,
  useErpUploadBatchUpdateMutation,
  useDownloadErpUploadedFileMutation,
  useUpdateColumnMappingMutation,
  useGetErpColumnMappingMutation,
  useUpdateErpSyncSettingsMutation,
  useLazyGetErpSyncSettingsQuery,
  useLazyGetAppsListQuery,
  useLazyGetSecureSmsConfigQuery,
  useUpdateSecureSmsConfigMutation,
  useInstallAppMutation,
  useUninstallAppMutation,
  useLazyGetCredentialsQuery,
  useGenerateCredentialsMutation,
  useChangeApiKeyStatusMutation,
  useDeleteApiKeyMutation,
  useUpdateProcessingStatusMutation,
  useUploadPdfTemplateMutation,
  useLazyGetPdfTemplateQuery,
  useGetPdfCoordinatesMutation,
  useUpdatePdfTemplateMappingMutation,
  useGenerateSFTPPasswordMutation,
  useLazyGetSFTPCredentialsQuery,
  useLazyGetColumnMappingStatusQuery,
  useLazyGetErpCompaniesQuery,
  useUpdateRazorPayCredentialsMutation,
  useLazyGetRazorPayCredentialsQuery,
  useAddOrgIdMutation,
  useGetZohoAuthStateMutation,
  useLazyGetZohoAuthUrlQuery,
  useLazyGetZosyncSettingsQuery,
  useUpdateZosyncSettingsMutation,
  useLazyGetZoApiWebhookConfigQuery,
  useUpdateZoApiWebhookConfigMutation,
  useLazyGetErpNextCredentialsQuery,
  useSaveErpNextCredentialsMutation,
  useSaveFetchDateMutation,
  useConfigureErpNextWebhooksMutation,
  useLazyGetErpExportMappingsQuery,
  useUpdateErpExportMappingsMutation,
  useUpdateERPAuthenticationMutation,
  useLazyGetERPAuthenticationQuery,
  useUpdateSAPHistoricalImportMutation,
  useStopSAPSyncMutation,
  useStartSAPSyncMutation,
  useConnectGoogleMutation,
  useLazyGetConnectorsQuery,
  useLazyGetGoogleSheetsPickerConfigQuery,
  useLazyRegisterGoogleSheetQuery,
  useDisconnectGoogleMutation,
  useLazyGetGoogleSheetQuery,
  useDeleteGoogleSheetMutation,
} = bulkImportApi;
